import { Component, HostListener, Inject, OnDestroy, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as moment from 'moment';
import { Subscription } from 'rxjs';
import { CustomRouter } from 'src/admin-front/ajs-upgraded-providers';
import { Scenario } from 'src/app/care/scenarios/models';
import { ScenariosService } from 'src/app/care/scenarios/scenarios.service';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { SearchBarService } from 'src/app/shared/components/search-bar/search-bar.service';
import { TableColumnTypeEnum, TableFilterTypeEnum } from 'src/app/shared/components/table/shared/enums';
import { TableConfig, TableFilters } from 'src/app/shared/components/table/shared/models';
import { TableLabelCellEnum } from 'src/app/shared/components/table/shared/models/table-label-cell.enum';
import { BasicService } from 'src/app/shared/services/basic.service';
import { CustomEvent } from './custom-event.model';
import { CustomEventsService } from './custom-events.service';
import { CustomEventTypeEnum } from './shared/custom-event-type.enum';

@Component({
  selector: 'app-custom-events',
  templateUrl: './custom-events.component.html',
  styleUrls: ['./custom-events.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CustomEventsComponent implements OnDestroy {
  scenariosMap: Map<number, Partial<Scenario>> = new Map();
  config: TableConfig = {
    displayedColumns: ['id', 'name', 'description', 'event_type', 'scenarios', 'actions'],
    columns: [
      { name: 'id', translationKey: 'parameters/custom-events/id', property: 'id' },
      {
        name: 'name',
        translationKey: 'parameters/custom-events/name',
        property: 'nameLink',
        type: TableColumnTypeEnum.LINK,
      },
      {
        name: 'description',
        translationKey: 'parameters/custom-events/description',
        property: 'description',
      },
      {
        name: 'event_type',
        translationKey: 'parameters/custom-events/type',
        property: 'eventTypeLabel',
        type: TableColumnTypeEnum.LABEL,
      },
      {
        name: 'scenarios',
        translationKey: 'parameters/custom-events/scenarios',
        property: 'scenarios',
        type: TableColumnTypeEnum.LINK_LIST,
      },
    ],
    actions: [
      {
        tooltip: 'delete',
        tooltipDisabled: 'parameters/custom-events/delete-disabled',
        isDisabled: (element: CustomEvent) => (element.scenario_ids || []).length,
        customClass: (element: CustomEvent) => element.event_type !== CustomEventTypeEnum.CUSTOM && 'hidden',
        clickFn: (element: CustomEvent) => this.delete(element.id),
        icon: 'delete',
        color: 'warn',
      },
    ],
  };
  filters: TableFilters = {
    event_type: {
      name: 'eventTypes',
      items: Object.keys(CustomEventTypeEnum).map(id => {
        const criterion = id.toLowerCase();
        return { id: criterion, label: this.basicService.translate(`parameters/custom-events/type/${criterion}`) };
      }),
      placeholder: 'parameters/custom-events/filters/type',
      type: TableFilterTypeEnum.MULTI_CHOICE,
    },
  };

  private subscriptions = new Subscription();

  @HostListener('window:resize')
  onWindowResizeUpdateActionsPosition() {
    this.searchBarService.checkScrollPosition();
  }

  constructor(
    public dialog: MatDialog,
    private alcmeonContextService: AlcmeonContextService,
    private customEventsService: CustomEventsService,
    private searchBarService: SearchBarService,
    private customRouter: CustomRouter,
    private basicService: BasicService,
    scenariosService: ScenariosService,
    @Inject('TopToolbarFactory') private topToolbarService: any
  ) {
    topToolbarService.setTitleTranslation('parameters/custom-events/title');
    scenariosService.findMarketingAndCareScenarios().subscribe(scenarios => {
      (scenarios || []).forEach(scenario => {
        const { id, name, url, site_id, flavor } = scenario;
        this.scenariosMap.set(id, { id, name, url, site_id, flavor });
      });
      this.searchBarService.submitQuery();
    });
    this.searchBarService.sort = { active: 'event_type', direction: 'desc' };
    this.searchBarService.referer$ = this.customEventsService.getCustomEvents;
    this.searchBarService.formatter = this.formatter;
  }

  private formatter = (element: CustomEvent) => {
    const { id, name, scenario_ids } = element;
    const scenariosList: Partial<Scenario>[] = (scenario_ids || [])
      .map(id => this.scenariosMap.get(id))
      .filter(i => i)
      .sort((a, b) => (moment(b['last_modification_date']).isSameOrAfter(a['last_modification_date']) ? 1 : -1));
    const linkList = (scenariosList || []).map(({ id, name, site_id, flavor }) => {
      const { companyId } = this.customRouter.params;
      const href = this.basicService.fullURLForRoutePath(
        `/#/${companyId}/fuse/parameters/account/${site_id}/${flavor}/${id}`
      );
      return { text: name, href, newTab: true };
    });
    element.scenarios = linkList;
    // URL related to the state auth.alcmeon.fuse.parameters.custom-events.details
    const customEventHref = this.basicService.fullURLForRoutePath(
      `/#/${this.alcmeonContextService.companyId}/fuse/parameters/custom-events/${id}`
    );
    element['nameLink'] = {
      text: name,
      href: customEventHref,
      newTab: false,
    };

    let color = null;
    switch (element.event_type) {
      case CustomEventTypeEnum.MARKETING:
        color = TableLabelCellEnum.RED_MARKETING;
        break;
      case CustomEventTypeEnum.AI:
        color = TableLabelCellEnum.BLUE;
        break;
      case CustomEventTypeEnum.CUSTOM:
        color = TableLabelCellEnum.YELLOW;
        break;
    }
    element.eventTypeLabel = {
      text: this.basicService.translate(`parameters/custom-events/type/${element.event_type}`),
      color,
    };
  };

  delete = (customEventId: number) => {
    const item: CustomEvent = this.searchBarService.itemsBS.value.find(({ id }) => id === customEventId);
    if (item) {
      const { name } = item;
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        panelClass: ['medium-size'],
        autoFocus: false,
        data: {
          title: this.basicService.translate(`parameters/custom-events/delete-title`),
          description: this.basicService.translate(`parameters/custom-events/delete-description`, { name }),
          confirmBtnText: this.basicService.translate('delete'),
          cancelBtnText: this.basicService.translate('close'),
        },
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          if (item) {
            const { id } = item;
            this.customEventsService.deleteCustomEvent(id).subscribe({
              next: () => {
                this.searchBarService.submitQuery();
              },
            });
          }
        }
      });
    }
  };

  primaryButtonAction = () => this.customRouter.go('auth.alcmeon.fuse.parameters.custom-events.create');

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
    this.searchBarService.onDidLeave();
    this.topToolbarService.clearTitle();
  }
}
